/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { Button, Divider, Form, Input, Modal, Row } from 'antd';
import {
  postFinancialStage,
  updateFinancialStage,
} from '../../data/store/account/accountActions';
import CustomInputNumber from '../../components/CustomInputNumber/CustomInputNumber';
import useFinPlandate from '../../hooks/useFinPlandate';
import dayjs from 'dayjs';

import { ReactComponent as Question } from './img/question-icon.svg';

const ModalCreateStage = ({
  planId,
  t,
  open,
  closeCreateModal,
  currentLang,
  financialPlan,
  category,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [needSaveInfo, setNeedSaveInfo] = useState(false);

  const { stageStartDate, stageEndDate } = useFinPlandate();
  const isRangeUnavailable = stageStartDate == null || stageEndDate == null;

  useEffect(() => {
    if (open !== 'new') {
      const stage = financialPlan?.stages?.find((i) => i.id === +open);
      stage && form.setFieldsValue(stage);
    }
  }, [open]);

  useEffect(() => {
    if (open === 'new' && !isRangeUnavailable) {
      form.setFieldsValue({
        start_year: stageStartDate,
        end_year: stageEndDate,
      });
    }
  }, [open, isRangeUnavailable, stageStartDate, stageEndDate]);

  const stratYear = Form.useWatch('start_year', form);
  const endYear = Form.useWatch('end_year', form);

  const getRange = useMemo(() => {
    if (endYear && stratYear && endYear > stratYear) {
      return endYear - stratYear;
    }
    return '';
  }, [stratYear, endYear]);

  const hasChanges = (allValues) => {
    const { name, start_year, end_year } = allValues;
    const isValid = name && start_year && end_year;
    const isChange = true;
    return isValid && isChange;
  };

  const { expensesTotalsByGlobalCategory, expensesTotalAmount } =
    useMemo(() => {
      if (open === 'new') {
        return { expensesTotalsByGlobalCategory: {}, expensesTotalAmount: 0 };
      }
      const stage = financialPlan?.stages?.find((i) => i.id === +open);
      const items = stage?.expenses ?? [];

      const totalsMap = items.reduce((acc, item) => {
        const key = item.global_category;
        acc[key] = (acc[key] ?? 0) + (item?.avg_amount_yearly_sek ?? 0);
        return acc;
      }, {});

      const total = Object.values(totalsMap).reduce((a, b) => a + b, 0);
      return {
        expensesTotalsByGlobalCategory: totalsMap,
        expensesTotalAmount: total,
      };
    }, [open, financialPlan]);

  const handleChange = (_, allValues) => {
    setNeedSaveInfo(hasChanges(allValues));
  };

  const handleClose = () => {
    form.resetFields();
    closeCreateModal();
  };

  const handleCreateUdateStage = () => {
    form.submit();
  };

  const onSubmit = () => {
    const data = form.getFieldsValue(true);
    console.log('🚀 ~ onSubmit ~ data:', data);
    if (data.id) {
      dispatch(
        updateFinancialStage({
          planId,
          stageId: data.id,
          data,
          callback: handleClose,
        }),
      );
    } else {
      dispatch(postFinancialStage({ planId, data, callback: handleClose }));
    }
  };

  return (
    <Modal
      width={700}
      className="modal-content-paper stage-modal"
      open={open}
      onCancel={closeCreateModal}
      closable={false}
      footer={null}
      zIndex={1620}
    >
      <h1 className="plan-create-modal-title">{t('ADD_LIFE_STAGE')}</h1>
      <Form
        form={form}
        name="finplan-stage-form"
        layout="vertical"
        onValuesChange={handleChange}
        onFinish={onSubmit}
      >
        <Form.Item
          name="name"
          className="custom-required full-width"
          required={false}
          rules={[
            {
              required: true,
              message: t('PLEASE_ENTER_A_NAME'),
            },
          ]}
        >
          <Input
            placeholder={t('NAME')}
            className="custom-input full-width finplan-input"
          />
        </Form.Item>
        <div className="finplan-year-label-row">
          <div className="finplan-year-label-cell">
            {t('FROM')}
            <span className="">{t('YEAR')}</span>
          </div>
          <Divider className="finplan-year-label-divider" />
          <div className="finplan-year-label-cell">
            {t('TO')}
            <span className="">{t('YEAR')}</span>
          </div>
        </div>
        <div className="finplan-year-row">
          <div className="finplan-year-cell">
            <Form.Item
              name="start_year"
              className="custom-required full-width"
              required={false}
              rules={[
                {
                  required: true,
                  message: t('PLEASE_ENTER_AMOUNT'),
                },
                () => ({
                  validator(_, value) {
                    if (isRangeUnavailable) {
                      return Promise.reject(new Error(t('NO_AVAILABLE_RANGE')));
                    }
                    if (value == null) return Promise.resolve();
                    if (value < stageStartDate || value > stageEndDate) {
                      return Promise.reject(new Error(t('VALUE_OUT_OF_RANGE')));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <CustomInputNumber
                min={stageStartDate ?? 0}
                max={stageEndDate ?? 0}
                placeholder={t('2025')}
                className="custom-inputnumber full-width finplan-input"
                controls={false}
                disabled={isRangeUnavailable}
              />
            </Form.Item>
          </div>
          <div className="finplan-year-cell range">
            {getRange} <span>{t('YRS')}</span>
          </div>
          <div className="finplan-year-cell">
            <Form.Item
              name="end_year"
              className="custom-required full-width"
              required={false}
              rules={[
                {
                  required: true,
                  message: t('PLEASE_ENTER_AMOUNT'),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (isRangeUnavailable) {
                      return Promise.reject(new Error(t('NO_AVAILABLE_RANGE')));
                    }
                    if (value == null) return Promise.resolve();
                    const startVal = getFieldValue('start_year');
                    if (startVal != null && value < startVal) {
                      return Promise.reject(new Error(t('END_BEFORE_START')));
                    }
                    if (value > stageEndDate) {
                      return Promise.reject(new Error(t('VALUE_OUT_OF_RANGE')));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <CustomInputNumber
                min={stratYear ?? stageStartDate ?? 0}
                max={stageEndDate ?? 0}
                placeholder={t('2025')}
                className="custom-inputnumber full-width finplan-input"
                controls={false}
                disabled={isRangeUnavailable}
              />
            </Form.Item>
          </div>
        </div>
      </Form>
      <div className="finplan-question">
        <span>{t('DEFINE_SPLIT')}</span>
        <Question />
      </div>
      <div className="finplan-categorycard-wrapper">
        {category?.expenses?.map(({ id, name }) => {
          return (
            <div key={id} className="finplan-category-card">
              <div className="finplan-category-card-title">
                <div className={`fp-dot-${id}`} />
                <span>{t(name[currentLang])}</span>
              </div>
              <div className="finplan-category-card-dig">
                {expensesTotalAmount > 0
                  ? Math.round(
                      ((expensesTotalsByGlobalCategory?.[id] ?? 0) * 100) /
                        expensesTotalAmount,
                    )
                  : 0}{' '}
                %
              </div>
            </div>
          );
        })}
      </div>
      <Row className="modal-newportfolio-button-wrapper">
        <Button
          type="text"
          className="modal-action-btn cancel-btn"
          onClick={handleClose}
        >
          {t('CANCEL')}
        </Button>
        <Button
          type="default"
          className="modal-action-btn save-btn"
          disabled={!needSaveInfo || isRangeUnavailable}
          onClick={handleCreateUdateStage}
        >
          {t('CONTINUE')}
        </Button>
      </Row>
    </Modal>
  );
};

export default ModalCreateStage;

ModalCreateStage.propTypes = {
  t: PropTypes.func,
  financialPlan: PropTypes.object,
  planId: PropTypes.number,
  open: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  closeCreateModal: PropTypes.func,
  currentLang: PropTypes.string,
  category: PropTypes.object,
};
